/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Box, Badge, Image} from '@chakra-ui/react'
import {useItemVariant} from '.'
import {FormattedMessage, useIntl} from 'react-intl'
import {HYBRID_PDP, ITEM_IMAGE_ASPECT, MAIN_IMAGES_IMAGE_TYPE} from '../../constants'
import {useProductDisplayImages} from '../../hooks/use-product-display-images'
import {productUrlBuilder} from '../../utils/url'
import HybridLink from '../hybrid-link'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({
    imageProps,
    ratio = ITEM_IMAGE_ASPECT,
    width = '80px',
    link = true,
    ...props
}) => {
    const intl = useIntl()
    const variant = useItemVariant()
    const productId = variant.master?.masterId || variant.productId || variant.id

    // We find the MAIN_IMAGES_IMAGE_TYPE images in the variant's image groups based on variationValues and pick the first one
    const image = useProductDisplayImages(
        variant,
        MAIN_IMAGES_IMAGE_TYPE,
        variant.variationValues,
        1
    )[0]

    const Component = link ? HybridLink : Box

    return (
        <Box backgroundColor="stokkeCore.white" {...props}>
            <AspectRatio ratio={ratio} width={width}>
                <Box position="relative">
                    {variant.c_isSale && (
                        <Badge
                            position="absolute"
                            top={0}
                            left={0}
                            marginLeft={2}
                            marginTop={2}
                            fontSize="10px"
                            variant="solid"
                            colorScheme="blue"
                        >
                            <FormattedMessage
                                defaultMessage="Sale"
                                id="item_image.label.sale"
                                description="A sale badge placed on top of a product image"
                            />
                        </Badge>
                    )}

                    {image && (
                        <Component
                            variant="image"
                            {...(link && {
                                page: HYBRID_PDP,
                                to: productUrlBuilder(
                                    {id: productId},
                                    intl.locale,
                                    variant.slugUrl || variant.c_slugUrl
                                ),
                                hybridHref: variant.slugUrl || variant.c_slugUrl
                            })}
                        >
                            <Image
                                alt={image.alt}
                                src={`${image.link}`}
                                ignoreFallback={true}
                                width="full"
                                {...imageProps}
                            />
                        </Component>
                    )}
                </Box>
            </AspectRatio>
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object,
    link: PropTypes.bool,
    ratio: PropTypes.number,
    width: PropTypes.any
}

export default ItemImage
